import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1585402969048-86f38f8bfc2e?crop=entropy&cs=srgb&fm=jpg&q=85";

const HERO_VIDEO =
  "https://videos.pexels.com/video-files/18197399/18197399-hd_1280_720_30fps.mp4";

const HERO_LOOPS = ["/media/hero-loop.webm", "/media/dunes-loop.webm", "/media/falls-loop.webm"];

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.4 + i * 0.15, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

function MaskedLine({ index, children, className = "" }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        custom={index}
        variants={lineVariant}
        initial="hidden"
        animate="show"
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

function TiltFrame() {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 18 });
  const sry = useSpring(ry, { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 10);
    rx.set(-py * 10);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, y: 40 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: 1.1, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className="hidden lg:block"
      style={{ perspective: 900 }}
    >
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => {
          rx.set(0);
          ry.set(0);
        }}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        className="relative w-[380px] overflow-hidden rounded-2xl border border-goldline shadow-[var(--shadow-card)]"
      >
        <img src={HERO_IMG} alt="Okavango Delta aerial" className="photo-treat h-[480px] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 right-5" style={{ transform: "translateZ(40px)" }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Okavango Delta · Botswana</p>
          <p className="mt-1 font-serif text-2xl text-cream">Where the river meets the desert</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function HeroVideoLoop() {
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    refs.current.forEach((v, i) => {
      if (!v) return;
      if (i === active) {
        v.currentTime = 0;
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [active]);

  const advance = () => setActive((a) => (a + 1) % HERO_LOOPS.length);

  return (
    <>
      {HERO_LOOPS.map((src, i) => (
        <video
          key={src}
          ref={(el) => (refs.current[i] = el)}
          data-testid={i === 0 ? "hero-background-video" : `hero-background-video-${i}`}
          muted
          playsInline
          preload={i === 0 ? "auto" : "metadata"}
          poster={HERO_IMG}
          onEnded={advance}
          onError={() => i === active && advance()}
          className={`hero-photo absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <source src={src} type="video/webm" />
          {i === 0 && <source src={HERO_VIDEO} type="video/mp4" />}
        </video>
      ))}
    </>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10 scale-110">
        <HeroVideoLoop />
        <div className="hero-veil-main absolute inset-0" />
        <div className="hero-veil-vertical absolute inset-0" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto grid w-full max-w-7xl items-center gap-16 px-4 pb-24 pt-36 sm:px-8 lg:grid-cols-[1.4fr_1fr] lg:px-16"
      >
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="kicker mb-8"
          >
            Southern Africa · B2B Travel Marketplace
          </motion.p>

          <h1 className="font-serif text-5xl font-semibold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            <MaskedLine index={0}>Where wild Africa</MaskedLine>
            <MaskedLine index={1}>
              meets the <em className="not-italic text-gold">business</em>
            </MaskedLine>
            <MaskedLine index={2}>of travel.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-muted sm:text-lg"
          >
            Tourism Connect unites lodges, tour operators, travel agencies and DMCs across
            Botswana, Namibia, South Africa and Zimbabwe — one marketplace, one live booking
            engine, endless itineraries.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-booking-subdomain-cta"
              href="https://booking.tourism-connect.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
            >
              <span className="live-dot h-2 w-2 rounded-full bg-green-600" />
              Launch Booking Engine <ArrowUpRight size={16} />
            </a>
            <a data-testid="hero-waitlist-cta" href="#waitlist" className="btn-ghost">
              Join the Partner Waitlist
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-goldline pt-6"
          >
            {[
              ["1,200+", "Vetted properties"],
              ["450+", "Trade partners"],
              ["4", "Countries connected"],
            ].map(([v, l]) => (
              <div key={l}>
                <p className="font-serif text-3xl text-gold">{v}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <TiltFrame />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <ArrowDown className="animate-bounce text-gold/60" size={18} />
      </motion.div>
    </section>
  );
}
